import { Game } from "./game/Game";
import { Network } from "./net/Network";
import { Home } from "./ui/Home";
import { HUD } from "./ui/HUD";
import { Chat } from "./ui/Chat";
import { KeyOverlay } from "./ui/KeyOverlay";
import { Settings } from "./ui/Settings";
import { InputSystem, primeLayoutLabels } from "./input/Input";
import { MobileControls } from "./input/MobileControls";
import { GyroControls } from "./input/GyroControls";
import { Radar } from "./ui/Radar";
import { initAnalytics, track, identifyPilot, resetIdentity } from "./analytics";
import * as THREE from "three";

initAnalytics();
track("game_loaded", { ua: navigator.userAgent.slice(0, 200) });

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const radar = new Radar();

const input = new InputSystem();
input.setSuspended(true); // disabled until PLAY
primeLayoutLabels(); // localizes the keymap overlay labels via getLayoutMap

// Stable per-browser pilot id used to identify in PostHog. Generated once
// per device and persisted; survives reloads.
function getOrCreatePilotId(): string {
  const k = "skybreak.pilotId";
  let v = localStorage.getItem(k);
  if (!v) { v = crypto.randomUUID(); localStorage.setItem(k, v); }
  return v;
}
const PILOT_ID = getOrCreatePilotId();

let net: Network | null = null;
let game: Game | null = null;
let myId = "";
let respawnAt = 0;
let inputSeq = 1;
let lastInputSentAt = 0;
let currentArena = "";
let joined = false;

const hud = new HUD(() => net?.send({ t: "respawn" }));
const chat = new Chat((text) => { net?.send({ t: "chat", text }); });
const keyOverlay = new KeyOverlay();
function applyAudioSettings() {
  if (!game) return;
  game.audio.setVolumes({
    master: input.settings.masterVolume,
    sfx: input.settings.sfxVolume,
    engine: input.settings.engineVolume,
    muted: input.settings.muted,
  });
}
const settings = new Settings(input, () => leaveArena(), applyAudioSettings);

const homeSettingsBtn = document.getElementById("homeSettingsBtn");
homeSettingsBtn?.addEventListener("click", () => settings.toggle());

// ===== Mobile / touch / gyro =====
const isMobile = MobileControls.isMobile();
const mobile = new MobileControls(input, {
  onSkill: (n) => input.queueSkill(n),
  onBoost: () => { if (joined) net?.send({ t: "boostToRing" }); },
});
const gyro = new GyroControls(input);

if (isMobile) {
  mobile.enable();
  // Surface the gyro toggle in Settings (hidden on desktop).
  const gyroRow = document.getElementById("gyroRow");
  const gyroCalRow = document.getElementById("gyroCalibrateRow");
  if (gyroRow) gyroRow.hidden = false;
  if (gyroCalRow) gyroCalRow.hidden = false;
  // Restore gyro setting on boot. Browser may still need a permission prompt
  // on iOS — handled in the toggle handler.
  if (input.settings.gyroAim) {
    // Don't auto-enable; we need a user gesture for iOS perms.
    input.settings.gyroAim = false;
    input.saveSettings();
  }
}

const gyroChk = document.getElementById("gyroChk") as HTMLInputElement | null;
const gyroCalibrateBtn = document.getElementById("gyroCalibrateBtn") as HTMLButtonElement | null;
gyroChk?.addEventListener("change", async () => {
  if (gyroChk.checked) {
    const ok = await gyro.enable();
    if (!ok) {
      gyroChk.checked = false;
      toast("Gyroscope unavailable / permission denied");
      return;
    }
    input.settings.gyroAim = true;
    input.saveSettings();
    track("gyro_enabled");
  } else {
    gyro.disable();
    input.settings.gyroAim = false;
    input.saveSettings();
    track("gyro_disabled");
  }
});
gyroCalibrateBtn?.addEventListener("click", () => {
  gyro.calibrate();
  toast("Gyro re-centered");
});

// Keep throttle visual in sync with the keyboard W/S throttle integration.
setInterval(() => { if (mobile.isEnabled()) mobile.refreshThrottleVisual(); }, 100);

// War Thunder-style aim cursor: visible only when in-game with mouseAim on.
const aimCursor = document.getElementById("aimCursor")!;
function updateAimCursor() {
  const active = joined && input.settings.mouseAim && !settings.isOpen() && !chat.open;
  if (active) {
    aimCursor.classList.remove("hidden");
    aimCursor.style.transform = `translate(${input.mouseX}px, ${input.mouseY}px)`;
    document.body.style.cursor = "none";
  } else {
    aimCursor.classList.add("hidden");
    document.body.style.cursor = "";
  }
  requestAnimationFrame(updateAimCursor);
}
requestAnimationFrame(updateAimCursor);

input.on("openChat", () => {
  if (settings.isOpen()) return;
  if (!joined) return;
  if (chat.open) { chat.close(); input.setLockedToUI(false); }
  else { chat.show(); input.setLockedToUI(true); }
});
input.on("toggleKeymap", () => keyOverlay.toggle(input.bindings));
input.on("openSettings", () => {
  if (chat.open) { chat.close(); input.setLockedToUI(false); return; }
  settings.toggle();
});
input.on("respawn", () => {
  if (respawnAt && Date.now() >= respawnAt) net?.send({ t: "respawn" });
});
input.on("boostToRing", () => {
  if (joined) net?.send({ t: "boostToRing" });
});

// ===== Boot: home screen with live multiplayer preview =====
const home = new Home(({ name, skin, arena }) => playClicked({ name, skin, arena }));

bootGame();

// Render the game world immediately so the home screen has a live arena behind it.
function bootGame() {
  game = new Game(canvas, {
    onLocalUpdate: (me) => {
      hud.updateLocal(me);
      if (me && !me.alive) { if (!respawnAt) respawnAt = Date.now() + 3000; }
      else if (me && me.alive) respawnAt = 0;
      if (me && joined && game) {
        // Engine sound follows engineRpm (with a tiny floor so idle still rumbles)
        game.audio.setEngineThrottle(Math.max(0.05, me.engineRpm));
      }
    },
    onScoreboard: (rows, id) => {
      hud.updateScoreboard(rows, id);
      const me = rows.find(p => p.id === id);
      if (me) {
        const myQuat = new THREE.Quaternion(me.qx, me.qy, me.qz, me.qw);
        radar.draw(rows, id, myQuat);
      } else {
        radar.draw(rows, id, new THREE.Quaternion());
      }
    },
  });
  game.setCameraMode("spectator");
  game.start();
  applyAudioSettings();

  // Connect to a default arena to spectate. User's arena choice on Home will
  // either keep this connection (if same arena) or reconnect to the chosen one.
  connectArena("skybreak");
}

function connectArena(arenaId: string) {
  if (net) { net.close(); net = null; }
  currentArena = arenaId;
  joined = false;
  myId = "";
  if (game) game.setCameraMode("spectator");

  net = new Network(arenaId);
  net.on({
    onWelcome: (m) => {
      myId = m.id;
      game!.setMyId(myId);
      game!.loadWorld(m.seed, m.mapType);
      // The minimap is baked inside loadWorld — push it into the radar.
      radar.setMap(game!.minimap);
    },
    onState: (m) => game!.applyState(m.players, m.bullets, m.targets, m.rings),
    onEvents: (m) => {
      game!.applyEvents(m.explosions, m.kills, m.lasers);
      for (const k of m.kills) hud.pushKill(k.killer, k.victim);
    },
    onChat: (m) => chat.push(m.name, m.text, !!m.system),
    onPong: () => hud.setPing(net!.ping),
    onError: (m) => toast(m.msg),
    onClose: () => { /* status change handles UI */ },
    onStatusChange: (status) => {
      hud.setNetStatus(status);
      if (status === "reconnecting" && joined) toast("Reconnecting…");
      if (status === "closed" && joined) toast("Connection lost");
    },
  });
  net.connect();
}

function playClicked({ name, skin, arena }: { name: string; skin: 0 | 1 | 2 | 3; arena: string }) {
  // If the chosen arena differs from the current spectator connection, reconnect.
  if (!net || arena !== currentArena) {
    connectArena(arena);
    // Wait for welcome before sending join, with a small retry
    const tryJoin = () => {
      if (myId) sendJoinAndStartPlay(name, skin);
      else setTimeout(tryJoin, 50);
    };
    tryJoin();
  } else {
    sendJoinAndStartPlay(name, skin);
  }
}

function sendJoinAndStartPlay(name: string, skin: 0 | 1 | 2 | 3) {
  net!.send({ t: "join", name, skin, arena: currentArena });
  joined = true;
  home.hide();
  hud.show();
  input.setSuspended(false);
  game!.setCameraMode("follow");
  startInputLoop();
  game!.audio.unlock();
  game!.audio.startEngineLoop();
  game!.audio.play("ui_click");
  identifyPilot(PILOT_ID, { name, skin });
  track("arena_joined", { arena: currentArena, skin, mobile: isMobile });
}

function leaveArena() {
  joined = false;
  hud.hide();
  input.setSuspended(true);
  home.show();
  game!.setCameraMode("spectator");
  game!.audio.stopEngineLoop();
  game!.resetPredictor();
  track("arena_left", { arena: currentArena });
  resetIdentity();
  connectArena(currentArena || "skybreak");
}

let inputLoopRunning = false;
function startInputLoop() {
  if (inputLoopRunning) return;
  inputLoopRunning = true;
  const tick = () => {
    if (!net || !joined) { inputLoopRunning = false; return; }
    const now = performance.now();
    const dt = Math.min(0.1, (now - (lastInputSentAt || now)) / 1000);
    lastInputSentAt = now;
    const i = input.sample(dt, inputSeq++);
    // Compute world-space aim direction from the cursor projected through
    // the camera. Sent to the server with each input so the laser can fire
    // toward what the player is actually looking at.
    if (game) {
      const aim = game.computeAimFromCursor(input.mouseX, input.mouseY);
      if (aim) { i.aimX = aim.x; i.aimY = aim.y; i.aimZ = aim.z; }
      game.setLocalInput(
        { pitch: i.pitch, yaw: i.yaw, roll: i.roll, throttle: i.throttle, autoLevel: i.autoLevel },
        i.skill === 1, // dash trigger edge
      );
    }
    net.send({ t: "input", i });
    setTimeout(tick, 33);
  };
  tick();
}

setInterval(() => {
  if (respawnAt) {
    const left = Math.max(0, (respawnAt - Date.now()) / 1000);
    hud.setRespawnCountdown(left);
  }
}, 100);

function toast(msg: string) {
  const el = document.getElementById("toast")!;
  el.textContent = msg;
  el.classList.remove("hidden");
  setTimeout(() => el.classList.add("hidden"), 2500);
}
